import classNames from 'classnames';
import React, { FunctionComponent, ReactNode } from 'react';
import { Trans } from 'react-i18next';

import { OwnerType } from '../../../types/__generated__';
import PAGE_TYPES from '../../constants/pageTypes';
import { usePageMeta } from '../../hooks';
import { useProduct } from '../../hooks/page/product/useProduct';
import { useCharity } from '../../hooks/page/useCharity';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { filterByTypename } from '../../utils/graphql';
import { CompactPageOwnerAvatar } from '../CompactPageOwnerAvatar/CompactPageOwnerAvatar';
import styles from './CompactPageOwnerAvatarStory.scss';

interface CompactPageOwnerAvatarProps {
  className?: string;
  linkToFrp?: boolean;
}

const CompactPageOwnerAvatarStory: FunctionComponent<CompactPageOwnerAvatarProps> = ({
  className,
  linkToFrp,
}) => {
  const { data: basePageData } = useBasePageQuery();
  const { name, url } = useOwner();

  const charity = basePageData?.page?.relationships?.beneficiaries?.nodes
    ? filterByTypename(
        basePageData?.page?.relationships?.beneficiaries?.nodes,
        'Charity',
      )
    : null;
  const charityName = charity?.[0]?.name;
  const charityUrl = charity?.[0]?.profilePageUrl;

  return (
    <div className={styles.container}>
      <div className={classNames(styles.avatarCharityName, className)}>
        <CompactPageOwnerAvatar />
        <div>
          {name && (
            <Owner as={url ? 'a' : 'span'} href={url}>
              <Trans i18nKey="shareModal.instagram.by">
                By {{ name: name }}
              </Trans>
            </Owner>
          )}
          {charityName && (
            <p className="jg-space-mbn" style={{ fontWeight: 400 }}>
              for{' '}
              {!linkToFrp ? (
                <span>{charityName}</span>
              ) : (
                <a href={charityUrl}>{charityName}</a>
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompactPageOwnerAvatarStory;

interface OwnerProps {
  children: ReactNode;
  as: 'span' | 'a';
  href?: string;
}

function Owner({ as, children, ...props }: OwnerProps) {
  const Component = as;

  return (
    <Component {...props} className="jg-h6 jg-space-mbn jg-hero__owner-title">
      {children}
    </Component>
  );
}

function useOwner() {
  const { data: basePageData } = useBasePageQuery();
  const { pageType } = usePageMeta();
  const { name: charityName, profilePageUrl } = useCharity();
  const {
    data: { ownerType },
  } = useProduct();

  switch (true) {
    case pageType === PAGE_TYPES.FUNDRAISING ||
      (pageType === PAGE_TYPES.ONE_PAGE && ownerType === OwnerType.Individual):
      return {
        name: basePageData?.page?.owner?.name,
      };
    case pageType === PAGE_TYPES.CAMPAIGN ||
      (pageType === PAGE_TYPES.ONE_PAGE &&
        ownerType === OwnerType.Organisation):
      return {
        name: charityName,
        url: profilePageUrl,
      };
    default:
      return {};
  }
}
